import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Clock } from 'lucide-react';
import { Button } from '../components/design-system';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { apiErrorMessage } from '../utils/helpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VerifyEmail = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setSession, isAuthenticated } = useAuth();

  const [status, setStatus] = useState('loading'); // loading, success, expired, invalid, error
  const [email, setEmail] = useState(null);
  const [isResending, setIsResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [alreadyVerified, setAlreadyVerified] = useState(false);
  // El token se consume al primer uso: evitar que un doble montaje (StrictMode) lo gaste
  const requestedRef = useRef(false);

  const verify = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/customers/verify-email/${token}`);
      const data = await response.json();

      if (data.valid) {
        setStatus('success');
        setEmail(data.email);
        setAlreadyVerified(!!data.already_verified);
        // El link suele abrirse en otra pestaña/contexto (p. ej. Safari desde Mail),
        // donde el popup no sabe que ya se registró
        localStorage.setItem('alphatica_registered', 'true');
        if (data.token && data.user) {
          setSession(data.token, data.user);
        }
      } else if (data.reason === 'expired_token') {
        setStatus('expired');
        setEmail(data.email || null);
      } else {
        setStatus('invalid');
      }
    } catch (err) {
      setStatus('error');
    }
  }, [token, setSession]);

  useEffect(() => {
    if (token) {
      if (requestedRef.current) return;
      requestedRef.current = true;
      verify();
    } else {
      setStatus('invalid');
    }
  }, [token, verify]);

  const handleResend = async () => {
    if (!email || isResending) return;
    setIsResending(true);
    try {
      const response = await fetch(`${API_URL}/api/public/customers/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'Te reenviamos el correo de confirmación');
        setResent(true);
      } else {
        toast.error(apiErrorMessage(data, 'No se pudo reenviar el correo'));
      }
    } catch (err) {
      toast.error('Error de conexión');
    } finally {
      setIsResending(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className="pt-32 pb-24 px-6 max-w-2xl mx-auto text-center" data-testid="verify-email-loading">
        <Loader2 className="w-16 h-16 mx-auto mb-6 text-brand-cobalt animate-spin" />
        <h1 className="font-display text-2xl md:text-3xl tracking-tight mb-4">
          Confirmando tu correo...
        </h1>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="pt-32 pb-24 px-6 max-w-2xl mx-auto text-center" data-testid="verify-email-success">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>
        <h1 className="font-display text-2xl md:text-3xl tracking-tight mb-4">
          {alreadyVerified ? 'Tu correo ya estaba confirmado' : '¡Correo confirmado!'}
        </h1>
        <p className="text-brand-black/60 mb-8">
          {alreadyVerified && !isAuthenticated
            ? 'Para entrar a tu cuenta en este dispositivo, pide un enlace a tu correo.'
            : 'Tu 10% de descuento (BIENVENIDO10) ya está activo para tu primera compra.'}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {alreadyVerified && !isAuthenticated ? (
            <Button variant="primary" onClick={() => navigate('/login')}>
              Entrar a mi cuenta
            </Button>
          ) : (
            <Button variant="primary" onClick={() => navigate('/products')}>
              Ir a la tienda
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (status === 'expired') {
    return (
      <div className="pt-32 pb-24 px-6 max-w-2xl mx-auto text-center" data-testid="verify-email-expired">
        <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Clock className="w-10 h-10 text-amber-600" />
        </div>
        <h1 className="font-display text-2xl md:text-3xl tracking-tight mb-4">
          El enlace expiró
        </h1>
        <p className="text-brand-black/60 mb-8">
          Este enlace de confirmación ya no es válido. Podemos enviarte uno nuevo.
        </p>
        <Button variant="primary" onClick={handleResend} disabled={isResending || resent}>
          {resent ? 'Correo reenviado' : isResending ? 'Enviando...' : 'Reenviar correo de confirmación'}
        </Button>
      </div>
    );
  }

  // invalid / error
  return (
    <div className="pt-32 pb-24 px-6 max-w-2xl mx-auto text-center" data-testid="verify-email-invalid">
      <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <XCircle className="w-10 h-10 text-red-600" />
      </div>
      <h1 className="font-display text-2xl md:text-3xl tracking-tight mb-4">
        Enlace inválido
      </h1>
      <p className="text-brand-black/60 mb-8">
        Este enlace de confirmación no es válido. Si ya te registraste, intenta desde el correo más reciente que recibiste.
      </p>
      <Button variant="ghost" onClick={() => navigate('/')}>
        Volver al inicio
      </Button>
    </div>
  );
};

export default VerifyEmail;
